import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllRequirements, createRequirement, updateRequirement, getAllEngagements } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Plus, ChevronRight } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import RequirementForm from '../../components/forms/RequirementForm';
import { GARMENT_TYPE, REQUIREMENT_STATUS, WORKFLOW_STAGE, formatDate, formatINR, label } from '../../constants/garmentCodes';

const STATUS = { DRAFT: 1, SUBMITTED: 2, CLARIFICATION: 4, ACCEPTED: 5 };
const FILTERS = [
  { key: '', text: 'All' },
  { key: STATUS.ACCEPTED, text: 'Accepted' },
  { key: STATUS.SUBMITTED, text: 'Submitted' },
  { key: STATUS.CLARIFICATION, text: 'Clarification' },
  { key: STATUS.DRAFT, text: 'Draft' },
];
const PRIORITY_DOT = { 1: 'bg-[#C0222B]', 2: 'bg-[#92560C]', 3: 'bg-[#9B9893]' };

export default function BrandRequirements() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const requirements = useSelector((state) => state.requirement.requirements);
  const loading = useSelector((state) => state.requirement.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brandId = useSelector((state) => state.brand.profile?.id);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllRequirements({ brandId, status }));
  }, [dispatch, brandId, status]);

  const create = (body) => {
    dispatch(createRequirement({ payload: { ...body, status: STATUS.SUBMITTED, stage: 2 } }));
    setAddOpen(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Product Requirements"
        subtitle={`${requirements.length} requirements`}
        actions={<Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>Create requirement</Button>}
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="flex gap-2 flex-wrap items-center">
          {FILTERS.map((f) => (
            <button
              key={f.text}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${status === f.key ? 'bg-[#1E2A3B] text-white border-transparent' : 'bg-white text-[#5C5A54] border-[#E5E3DE] hover:bg-[#F4F3F0]'}`}
            >
              {f.text}
            </button>
          ))}
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="BrandRequirements"
            columns={[
              { id: 'priority', label: 'PRIORITY', align: 'center', width: 60 },
              { id: 'reqId', label: 'CODE', align: 'left' },
              { id: 'productName', label: 'PRODUCT NAME', align: 'left' },
              { id: 'type', label: 'TYPE', align: 'left' },
              { id: 'manufacturer', label: 'MANUFACTURER', align: 'left' },
              { id: 'qty', label: 'QTY', align: 'left' },
              { id: 'targetCost', label: 'TARGET COST', align: 'left' },
              { id: 'launchDate', label: 'LAUNCH DATE', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'stage', label: 'STAGE', align: 'left' },
              { id: 'action', label: 'ACTIONS', align: 'center', disableFilter: true },
            ]}
            rows={requirements.map((req) => {
              const stat = label(REQUIREMENT_STATUS, req.status);
              const stage = label(WORKFLOW_STAGE, req.stage);
              const canSubmit = req.status === STATUS.DRAFT || req.status === STATUS.CLARIFICATION;
              return {
                id: req.id,
                rColumns: [
                  <div key="pri" label={String(req.priority ?? '')} className="flex items-center justify-center w-full h-full px-2 py-1"><div className={`w-2 h-2 rounded-full ${PRIORITY_DOT[req.priority] || 'bg-[#E5E3DE]'}`} /></div>,
                  <div key="id" label={req.code || ''} className={`flex items-center w-full h-full px-2 py-1 font-mono text-xs text-[#3D52A0] font-semibold ${req.__optimistic ? 'opacity-60' : ''}`}>{req.code || `#${req.id}`}</div>,
                  <div key="prod" label={req.name || ''} className="flex flex-col justify-center items-start w-full h-full px-2 py-1">
                    <p className="font-semibold text-[#1A1915]">{req.name}</p>
                    <p className="text-xs text-[#9B9893]">{[req.collection, req.season].filter(Boolean).join(' • ')}</p>
                  </div>,
                  <div key="type" label={label(GARMENT_TYPE, req.garmentType)} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{label(GARMENT_TYPE, req.garmentType)}</div>,
                  <div key="mfg" label={req.manufacturerName || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{req.manufacturerName || '—'}</div>,
                  <div key="qty" label={String(req.quantity ?? '')} className="flex items-center w-full h-full px-2 py-1 text-xs font-medium">{req.quantity?.toLocaleString('en-IN') ?? '—'}</div>,
                  <div key="cost" label={formatINR(req.targetCost)} className="flex items-center w-full h-full px-2 py-1 text-xs font-medium">{formatINR(req.targetCost)}</div>,
                  <div key="date" label={req.launchDate || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{formatDate(req.launchDate)}</div>,
                  <div key="stat" label={stat} className="flex items-center w-full h-full px-2 py-1"><Badge variant={statusToBadge(stat)}>{stat}</Badge></div>,
                  <div key="stage" label={stage} className="flex items-center w-full h-full px-2 py-1"><Badge variant="gray">{stage}</Badge></div>,
                  <div key="act" label="" className="flex items-center justify-center gap-1 w-full h-full px-2 py-1">
                    {canSubmit && !req.__optimistic && (
                      <Button variant="outline" size="xs" onClick={() => dispatch(updateRequirement({ payload: { id: req.id, status: STATUS.SUBMITTED } }))}>Submit</Button>
                    )}
                    {!req.__optimistic && <Button variant="ghost" size="xs" iconRight={<ChevronRight size={12} />} onClick={() => navigate(`/brand/fabrics?requirementId=${req.id}`)}>Fabrics</Button>}
                  </div>,
                ],
              };
            })}
          />
          {!loading && requirements.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No requirements</p>}
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Create Product Requirement" size="lg">
        <RequirementForm engagements={engagements} onSubmit={create} onCancel={() => setAddOpen(false)} submitLabel="Submit Requirement" />
      </Modal>
    </div>
  );
}
